import * as React from 'react'
import { AlertCircle, Download, FileWarning, Loader2 } from 'lucide-react'
import {
  SOURCE_REPRESENTATION_OFFICE_PDF,
  SOURCE_REPRESENTATION_WORKBOOK_PDF,
  SourceDocUnavailableError,
  fetchIngestSource,
  saveBlob,
  type FetchIngestSourceResult,
  type SourceRepresentation
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { pdfPreviewHref } from '../../lib/document-preview'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog'
import {
  isPdfRemappableSourcePath,
  isSpreadsheetWorkbookPath,
  prepareTextPreview,
  shouldFetchSourceDocForPreview,
  sourceDocKind,
  sourceFileBasename,
  type SourceDocKind
} from './source-doc-kind'

export type SourceDocSourceLoader = () => Promise<
  Pick<FetchIngestSourceResult, 'blob' | 'contentType' | 'representation' | 'originalPath'>
>

export interface SourceDocViewerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Job-corpus mode. Required unless `loadSource` is provided.
   * Existing Plant / Agent callsites keep passing `jobId + sourceFile`.
   */
  jobId?: string
  /** Provenance path or display name; passed verbatim for job-corpus fetches. */
  sourceFile: string
  sourcePage?: number
  sourceExcerpt?: string
  /**
   * Optional caller-supplied note (disposition reason, skip rationale, …).
   * Rendered as a header strip when present; Plant View opens omit it.
   */
  note?: string
  /** Optional disposition status that tints the note strip (pending/ingested/…). */
  noteStatus?: string
  /**
   * Pluggable loader for non-job sources (e.g. site-ingest object keys).
   * When set, replaces the default job-corpus fetch.
   */
  loadSource?: SourceDocSourceLoader
  /** Show “Download original” for remapped job-corpus previews. Default true when jobId is set. */
  supportsOriginalDownload?: boolean
}

/** How often to re-check for a generated PDF while preprocess is in flight. */
export const PREPROCESS_POLL_INTERVAL_MS = 2_000
/**
 * Max time to wait for a remappable source's PDF derivative before falling
 * back to download-only. Matches the LibreOffice conversion timeout budget.
 */
export const PREPROCESS_WAIT_MS = 90_000

function noteStripTone(status: string | undefined): string {
  switch (status) {
    case 'pending':
      return 'bg-amber-500/15 text-amber-800 dark:text-amber-200'
    case 'ingested':
      return 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-200'
    case 'skipped':
      return 'bg-muted text-muted-foreground'
    case 'unsupported':
      return 'bg-rose-500/15 text-rose-800 dark:text-rose-200'
    default:
      return 'bg-muted/60 text-muted-foreground'
  }
}

function asGeneratedPdfRepresentation(
  representation: string | undefined
): SourceRepresentation | undefined {
  if (
    representation === SOURCE_REPRESENTATION_WORKBOOK_PDF ||
    representation === SOURCE_REPRESENTATION_OFFICE_PDF
  ) {
    return representation
  }
  return undefined
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

type LoadState =
  | { status: 'idle' }
  | { status: 'loading' }
  | {
      status: 'ready'
      blob: Blob
      contentType: string
      objectUrl: string
      kind: SourceDocKind
      textPreview?: { text: string; truncated: boolean }
      /** Present when the body is a generated PDF for `sourceFile`. */
      generatedPdfRepresentation?: SourceRepresentation
    }
  /** Remappable path; generated PDF not ready yet — polling. */
  | { status: 'preprocessing' }
  /** Path says unpreviewable; corpus bytes not fetched yet. */
  | { status: 'download-only' }
  | { status: 'unavailable' }
  | { status: 'error'; message: string }
  | { status: 'image-failed'; blob: Blob; contentType: string; objectUrl: string }

export function SourceDocViewer({
  open,
  onOpenChange,
  jobId,
  sourceFile,
  sourcePage,
  sourceExcerpt,
  note,
  noteStatus,
  loadSource,
  supportsOriginalDownload
}: SourceDocViewerProps): React.ReactElement {
  const [load, setLoad] = React.useState<LoadState>({ status: 'idle' })
  const [reloadToken, setReloadToken] = React.useState(0)
  const [downloading, setDownloading] = React.useState(false)
  const [downloadingOriginal, setDownloadingOriginal] = React.useState(false)
  const objectUrlRef = React.useRef<string | null>(null)
  const loadSourceRef = React.useRef(loadSource)
  loadSourceRef.current = loadSource
  const allowOriginalDownload = supportsOriginalDownload ?? (!!jobId && !loadSource)

  const revokeCurrent = React.useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = null
    }
  }, [])

  React.useEffect(() => {
    if (!open) {
      revokeCurrent()
      setLoad({ status: 'idle' })
      setDownloading(false)
      setDownloadingOriginal(false)
      return
    }

    let cancelled = false
    revokeCurrent()
    setDownloading(false)

    // Skip the network round-trip for paths we know we can't preview.
    // Workbooks and office docs still fetch — the server may remap them to a
    // generated PDF.
    if (!shouldFetchSourceDocForPreview(sourceFile)) {
      setLoad({ status: 'download-only' })
      return
    }

    setLoad({ status: 'loading' })
    const remappable = !loadSourceRef.current && isPdfRemappableSourcePath(sourceFile)
    const deadline = Date.now() + PREPROCESS_WAIT_MS

    void (async () => {
      try {
        for (;;) {
          if (cancelled) return
          const result = loadSourceRef.current
            ? await loadSourceRef.current()
            : jobId
              ? await fetchIngestSource(jobId, sourceFile)
              : (() => {
                  throw new Error('SourceDocViewer requires jobId or loadSource')
                })()
          if (cancelled) return

          const { blob, contentType, representation } = result
          const kind = sourceDocKind(contentType, sourceFile)
          // Prefer the explicit remap header; if it's missing (e.g. not exposed
          // cross-origin) but the body is already a PDF for a remappable path,
          // the derivative is ready — don't keep polling.
          let generatedPdfRepresentation = asGeneratedPdfRepresentation(representation)
          if (!generatedPdfRepresentation && remappable && kind === 'pdf') {
            generatedPdfRepresentation = isSpreadsheetWorkbookPath(sourceFile)
              ? SOURCE_REPRESENTATION_WORKBOOK_PDF
              : SOURCE_REPRESENTATION_OFFICE_PDF
          }

          // Remappable path without a generated PDF yet — wait / poll.
          if (remappable && kind !== 'pdf' && !generatedPdfRepresentation) {
            if (Date.now() >= deadline) {
              setLoad({ status: 'download-only' })
              return
            }
            setLoad({ status: 'preprocessing' })
            await sleep(PREPROCESS_POLL_INTERVAL_MS)
            continue
          }

          const objectUrl = URL.createObjectURL(blob)
          objectUrlRef.current = objectUrl
          if (kind === 'text') {
            const raw = await blob.text()
            if (cancelled) {
              URL.revokeObjectURL(objectUrl)
              return
            }
            setLoad({
              status: 'ready',
              blob,
              contentType,
              objectUrl,
              kind,
              textPreview: prepareTextPreview(raw)
            })
            return
          }
          setLoad({
            status: 'ready',
            blob,
            contentType,
            objectUrl,
            kind,
            ...(generatedPdfRepresentation ? { generatedPdfRepresentation } : {})
          })
          return
        }
      } catch (err: unknown) {
        if (cancelled) return
        if (err instanceof SourceDocUnavailableError) {
          setLoad({ status: 'unavailable' })
          return
        }
        setLoad({
          status: 'error',
          message: err instanceof Error ? err.message : 'Failed to load document'
        })
      }
    })()

    return () => {
      cancelled = true
      revokeCurrent()
    }
  }, [open, jobId, sourceFile, loadSource, reloadToken, revokeCurrent])

  const basename = sourceFileBasename(sourceFile)
  const generatedPdfRepresentation =
    load.status === 'ready' ? load.generatedPdfRepresentation : undefined
  const generatedPdfPreview = generatedPdfRepresentation !== undefined
  const generatedPdfNoun =
    generatedPdfRepresentation === SOURCE_REPRESENTATION_WORKBOOK_PDF
      ? 'spreadsheet'
      : generatedPdfRepresentation === SOURCE_REPRESENTATION_OFFICE_PDF
        ? 'document'
        : null
  const blobForDownload =
    load.status === 'ready' || load.status === 'image-failed' ? load.blob : null
  const showOriginalDownload =
    allowOriginalDownload && (generatedPdfPreview || load.status === 'preprocessing')
  const canDownload = blobForDownload !== null || load.status === 'download-only'

  const fetchBytes = React.useCallback(
    async (original?: boolean) => {
      if (loadSourceRef.current) {
        return loadSourceRef.current()
      }
      if (!jobId) {
        throw new Error('SourceDocViewer requires jobId or loadSource')
      }
      return original
        ? fetchIngestSource(jobId, sourceFile, { original: true })
        : fetchIngestSource(jobId, sourceFile)
    },
    [jobId, sourceFile]
  )

  const handleDownload = () => {
    if (downloading) return

    if (blobForDownload) {
      // When showing a remapped PDF, "Download" still means the viewed bytes;
      // original download is a separate action.
      const name = generatedPdfPreview
        ? `${basename.includes('.') ? basename.slice(0, basename.lastIndexOf('.')) : basename}.pdf`
        : basename
      saveBlob(blobForDownload, name)
      return
    }

    if (load.status !== 'download-only') return

    setDownloading(true)
    void (async () => {
      try {
        const { blob } = await fetchBytes()
        saveBlob(blob, basename)
      } catch (err: unknown) {
        if (err instanceof SourceDocUnavailableError) {
          setLoad({ status: 'unavailable' })
          return
        }
        setLoad({
          status: 'error',
          message: err instanceof Error ? err.message : 'Failed to load document'
        })
      } finally {
        setDownloading(false)
      }
    })()
  }

  const handleDownloadOriginal = () => {
    if (downloadingOriginal || !allowOriginalDownload) return
    setDownloadingOriginal(true)
    void (async () => {
      try {
        const { blob } = await fetchBytes(true)
        saveBlob(blob, basename)
      } catch {
        // Best-effort — leave the preview open; toast would need a shared bus.
      } finally {
        setDownloadingOriginal(false)
      }
    })()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[min(94vh,68.75rem)] w-[min(96vw,90rem)] max-w-[min(96vw,90rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[min(96vw,90rem)]"
        showCloseButton
      >
        <DialogHeader className="shrink-0 gap-1.5 border-b px-6 py-3 text-left">
          <div className="flex flex-wrap items-center gap-2 pr-8">
            <DialogTitle className="m-0 truncate text-base">{basename}</DialogTitle>
            {typeof sourcePage === 'number' && sourcePage > 0 ? (
              <Badge variant="secondary">p. {sourcePage}</Badge>
            ) : null}
          </div>
          <DialogDescription className="m-0 truncate font-mono text-11 text-ink-4">
            {sourceFile}
          </DialogDescription>
          {generatedPdfPreview && generatedPdfNoun ? (
            <p className="m-0 mt-1.5 rounded-md bg-sky-500/10 px-2.5 py-1.5 text-xs leading-snug text-sky-900 dark:text-sky-100">
              <span className="font-medium">Viewing generated PDF</span>
              <span className="opacity-70"> — </span>
              Auto-opened the PDF layout preview for this {generatedPdfNoun}. The original file is
              unchanged; use Download original to get it.
            </p>
          ) : null}
          {load.status === 'preprocessing' ? (
            <p className="m-0 mt-1.5 rounded-md bg-amber-500/10 px-2.5 py-1.5 text-xs leading-snug text-amber-900 dark:text-amber-100">
              <span className="font-medium">Preview still preprocessing</span>
              <span className="opacity-70"> — </span>
              Generating a PDF layout preview for this file. This dialog will open it when ready.
            </p>
          ) : null}
          {note ? (
            <p
              className={`m-0 mt-1.5 rounded-md px-2.5 py-1.5 text-xs leading-snug ${noteStripTone(noteStatus)}`}
            >
              <span className="font-medium opacity-90">Note</span>
              <span className="opacity-70"> — </span>
              {note}
            </p>
          ) : null}
        </DialogHeader>

        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          <div className="min-h-0 min-w-0 flex-1 bg-bg-sunk/40">
            <ViewerBody
              load={load}
              sourcePage={sourcePage}
              sourceFile={sourceFile}
              basename={basename}
              downloading={downloading}
              onRetry={() => setReloadToken((n) => n + 1)}
              onImageError={() => {
                if (load.status !== 'ready' || load.kind !== 'image') return
                setLoad({
                  status: 'image-failed',
                  blob: load.blob,
                  contentType: load.contentType,
                  objectUrl: load.objectUrl
                })
              }}
              onDownload={handleDownload}
            />
          </div>
          {sourceExcerpt ? (
            <aside className="max-h-[22vh] shrink-0 overflow-auto border-t px-4 py-3 md:max-h-none md:w-56 md:border-t-0 md:border-l lg:w-60">
              <div className="text-10 font-semibold uppercase tracking-[0.06em] text-ink-4">
                Excerpt
              </div>
              <blockquote className="m-0 mt-2 border-l-2 border-border pl-3 text-13 leading-relaxed text-ink-2 italic">
                {sourceExcerpt}
              </blockquote>
            </aside>
          ) : null}
        </div>

        <DialogFooter className="shrink-0 border-t px-6 py-3 sm:justify-between">
          <span className="text-11 text-ink-4">
            {load.status === 'ready'
              ? generatedPdfPreview
                ? 'application/pdf (generated preview)'
                : load.contentType || 'unknown type'
              : load.status === 'preprocessing'
                ? 'waiting for generated preview'
                : null}
          </span>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {showOriginalDownload ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={downloadingOriginal}
                onClick={handleDownloadOriginal}
              >
                <Download className="size-3.5" aria-hidden />
                {downloadingOriginal ? 'Downloading…' : 'Download original'}
              </Button>
            ) : null}
            {load.status !== 'preprocessing' ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!canDownload || downloading}
                onClick={handleDownload}
              >
                <Download className="size-3.5" aria-hidden />
                {downloading ? 'Downloading…' : generatedPdfPreview ? 'Download PDF' : 'Download'}
              </Button>
            ) : null}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ViewerBody({
  load,
  sourcePage,
  sourceFile,
  basename,
  downloading,
  onRetry,
  onImageError,
  onDownload
}: {
  load: LoadState
  sourcePage?: number
  sourceFile: string
  basename: string
  downloading: boolean
  onRetry: () => void
  onImageError: () => void
  onDownload: () => void
}): React.ReactElement {
  if (load.status === 'idle' || load.status === 'loading') {
    return (
      <CenteredState icon={<Loader2 className="size-6 animate-spin text-ink-3" aria-hidden />}>
        Loading document…
      </CenteredState>
    )
  }

  if (load.status === 'preprocessing') {
    return (
      <CenteredState icon={<Loader2 className="size-6 animate-spin text-ink-3" aria-hidden />}>
        <div className="font-medium text-ink">{basename}</div>
        <p className="m-0 mt-2 max-w-sm text-sm text-ink-3">
          Preview is still preprocessing. Waiting for the generated PDF…
        </p>
      </CenteredState>
    )
  }

  if (load.status === 'unavailable') {
    return (
      <CenteredState icon={<FileWarning className="size-8 text-ink-3" aria-hidden />}>
        Document isn&apos;t available for this job
      </CenteredState>
    )
  }

  if (load.status === 'error') {
    return (
      <CenteredState icon={<AlertCircle className="size-8 text-st-down" aria-hidden />}>
        <span className="block">{load.message}</span>
        <Button type="button" variant="outline" size="sm" className="mt-3" onClick={onRetry}>
          Retry
        </Button>
      </CenteredState>
    )
  }

  if (
    load.status === 'download-only' ||
    load.status === 'image-failed' ||
    (load.status === 'ready' && load.kind === 'download')
  ) {
    const imageFail = load.status === 'image-failed'
    const timedOutRemap = load.status === 'download-only' && isPdfRemappableSourcePath(sourceFile)
    return (
      <DownloadOnlyState
        basename={basename}
        message={
          imageFail
            ? "Couldn't preview this image. Download it to open in another app."
            : timedOutRemap
              ? "A generated PDF preview isn't available for this file. Download it to open in another app."
              : "This document can't be previewed here. Download it to open in another app."
        }
        downloading={downloading}
        onDownload={onDownload}
      />
    )
  }

  if (load.kind === 'pdf') {
    return (
      <iframe
        src={pdfPreviewHref(load.objectUrl, sourcePage)}
        title={`${basename} preview`}
        className="size-full border-0"
      />
    )
  }

  if (load.kind === 'image') {
    return (
      <div className="flex size-full items-center justify-center overflow-auto p-4">
        <img
          src={load.objectUrl}
          alt={basename}
          className="max-h-full max-w-full object-contain"
          onError={onImageError}
        />
      </div>
    )
  }

  const preview = load.textPreview
  return (
    <div className="size-full overflow-auto p-4">
      {preview?.truncated ? (
        <div className="mb-3 rounded-md border border-amber-400/40 bg-amber-500/10 px-3 py-2 text-12 text-amber-800 dark:text-amber-300">
          Preview truncated to the first ~2 MB. Download the file for the full document.
        </div>
      ) : null}
      <pre className="m-0 whitespace-pre-wrap break-words font-mono text-12 leading-relaxed text-ink-2">
        {preview?.text ?? ''}
      </pre>
    </div>
  )
}

function DownloadOnlyState({
  basename,
  message,
  downloading,
  onDownload
}: {
  basename: string
  message: string
  downloading: boolean
  onDownload: () => void
}): React.ReactElement {
  return (
    <CenteredState icon={<FileWarning className="size-8 text-ink-3" aria-hidden />}>
      <div className="font-medium text-ink">{basename}</div>
      <p className="m-0 mt-2 max-w-sm text-sm text-ink-3">{message}</p>
      <Button type="button" className="mt-4" disabled={downloading} onClick={onDownload}>
        <Download className="size-3.5" aria-hidden />
        {downloading ? 'Downloading…' : 'Download'}
      </Button>
    </CenteredState>
  )
}

function CenteredState({
  icon,
  children
}: {
  icon: React.ReactNode
  children: React.ReactNode
}): React.ReactElement {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-12 text-center">
      {icon}
      <div className="max-w-md text-sm text-ink-3">{children}</div>
    </div>
  )
}
